"use client";

import { useState } from "react";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { IV_DRIP_RATE_CALCULATOR_STRINGS } from "@/components/tools/iv-drip-rate-calculator";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  dripRate,
  flowRate,
  infusionMinutes,
  roundHalfUp,
  toDuration,
} from "@/lib/tool-kit/stats-clinical/iv-drip";

/*
 * Textbook-style practice problems: a fixed list of made-up orders, shown one at a time,
 * with the answer hidden until asked for. Nothing here is random, so the server and the
 * browser render the same first problem.
 */

type Strings = typeof IV_DRIP_RATE_CALCULATOR_STRINGS;

type Problem =
  | { kind: "gtt"; volume: number; minutes: number; dropFactor: number }
  | { kind: "mlh"; volume: number; minutes: number }
  | { kind: "time"; volume: number; rate: number };

const PROBLEMS: readonly Problem[] = [
  { kind: "gtt", volume: 1000, minutes: 480, dropFactor: 15 },
  { kind: "mlh", volume: 500, minutes: 240 },
  { kind: "gtt", volume: 250, minutes: 90, dropFactor: 20 },
  { kind: "time", volume: 600, rate: 75 },
  { kind: "gtt", volume: 100, minutes: 30, dropFactor: 60 },
  { kind: "mlh", volume: 750, minutes: 330 },
  { kind: "gtt", volume: 1000, minutes: 600, dropFactor: 10 },
  { kind: "time", volume: 300, rate: 80 },
];

export function IvPractice({
  strings,
  fmt,
  locale,
}: {
  strings: Strings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
  locale: string;
}) {
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const problem = PROBLEMS[index];
  const duration = (minutes: number) => {
    const { hours, minutes: rest } = toDuration(minutes);
    const parts = [
      hours > 0
        ? tPlural(strings.hoursCount, locale, hours, {
            count: fmt(hours),
          }).trim()
        : "",
      rest > 0 || hours === 0
        ? tPlural(strings.minutesCount, locale, rest, {
            count: fmt(rest),
          }).trim()
        : "",
    ];
    return parts.filter((part) => part !== "").join(" ");
  };

  let question: string;
  let answer: string;
  if (problem.kind === "gtt") {
    const raw = dripRate(problem.volume, problem.dropFactor, problem.minutes);
    question = t(strings.problemGtt, {
      volume: fmt(problem.volume),
      time: duration(problem.minutes),
      df: fmt(problem.dropFactor),
    });
    answer = t(strings.answerGtt, {
      volume: fmt(problem.volume),
      df: fmt(problem.dropFactor),
      minutes: fmt(problem.minutes),
      raw: fmt(raw, { maximumFractionDigits: 2 }),
      rounded: fmt(roundHalfUp(raw, 0)),
    });
  } else if (problem.kind === "mlh") {
    const raw = flowRate(problem.volume, problem.minutes);
    question = t(strings.problemMlh, {
      volume: fmt(problem.volume),
      time: duration(problem.minutes),
    });
    answer = t(strings.answerMlh, {
      volume: fmt(problem.volume),
      hours: fmt(problem.minutes / 60, { maximumFractionDigits: 3 }),
      raw: fmt(raw, { maximumFractionDigits: 2 }),
      rounded: fmt(roundHalfUp(raw, 1), { maximumFractionDigits: 1 }),
    });
  } else {
    const minutes = infusionMinutes(problem.volume, problem.rate);
    question = t(strings.problemTime, {
      volume: fmt(problem.volume),
      rate: fmt(problem.rate),
    });
    answer = t(strings.answerTime, {
      volume: fmt(problem.volume),
      rate: fmt(problem.rate),
      hours: fmt(minutes / 60, { maximumFractionDigits: 3 }),
      duration: duration(minutes),
    });
  }

  return (
    <section className={`${CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>
        {t(strings.practiceHeading, {
          number: fmt(index + 1),
          total: fmt(PROBLEMS.length),
        })}
      </h3>
      <p className="text-foreground text-sm leading-relaxed">{question}</p>
      <div className="flex flex-wrap gap-3">
        <button
          aria-expanded={revealed}
          className={BUTTON_SMALL}
          onClick={() => setRevealed((value) => !value)}
          type="button"
        >
          {revealed ? strings.hideAnswer : strings.showAnswer}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setIndex((value) => (value + 1) % PROBLEMS.length);
            setRevealed(false);
          }}
          type="button"
        >
          {strings.nextProblem}
        </button>
      </div>
      {revealed ? (
        <p
          aria-live="polite"
          className="font-mono text-foreground text-sm leading-relaxed"
        >
          {answer}
        </p>
      ) : null}
      <p className="text-muted-foreground text-xs">{strings.practiceNote}</p>
    </section>
  );
}
